/**
 * ChatListHeader Component
 *
 * Header section for the Chat list page, containing:
 * - Page title ("Clipboard Chat")
 * - Search bar for filtering chats by workplace name
 * - Filter tabs (All, Flex, Jobs, Support)
 *
 * The Support filter tab has special handling via the `onSupportClick` callback,
 * which can trigger the mid-shift intervention bottom sheet when appropriate.
 */

import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";

import { type ChatChannelCustomTypesFilter } from "../types";
import { ChatListFilterTabs } from "./FilterTabs";
import { ChatListSearchBar } from "./SearchBar";

interface ChatListHeaderProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterType: ChatChannelCustomTypesFilter;
  onFilterTypeChange: (type: ChatChannelCustomTypesFilter) => void;
  /**
   * Optional callback for when the Support pill is clicked.
   * Passed through to ChatListFilterTabs to enable conditional behavior
   * (show intervention vs. open Zendesk).
   */
  onSupportClick?: () => void;
}

export function ChatListHeader(props: ChatListHeaderProps) {
  const { searchValue, onSearchChange, filterType, onFilterTypeChange, onSupportClick } = props;

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1 }}>
      <Stack
        direction="column"
        spacing={4}
        sx={{
          padding: 5,
          paddingBottom: 3,
        }}
      >
        <Text semibold variant="h4">
          Clipboard Chat
        </Text>

        <ChatListSearchBar value={searchValue} onChange={onSearchChange} />

        <ChatListFilterTabs
          filterType={filterType}
          onFilterTypeChange={onFilterTypeChange}
          onSupportClick={onSupportClick}
        />
      </Stack>
    </Box>
  );
}
